/* eslint-disable max-params */
import type { ChatAppRequest, ServerMessage } from "../models"

import { logger } from "@/lib/logger"

import { WebSocketClient } from "../web-socket-client"
import { BACKEND_URI, getHeaders } from "./_shared"

const EXPLORER_WARM_MIN_INTERVAL_MS = 2500
const EXPLORER_WARM_RETRY_DELAY_MS = 1000
let lastExplorerWarmAtMs = 0

/**
 * STREAMING: Starts research workflow via WebSocket (real-time streaming)
 * Note: This is a WebSocket operation, not a standard query/mutation
 * @param request - Chat request data
 * @param files - Optional files to include
 * @param idToken - Authentication token
 * @param signal - Abort signal for cancellation
 * @param onMessage - Optional callback for incoming messages
 * @returns Response promise
 * @category Streaming
 */
export async function startResearchApi(
  request: ChatAppRequest,
  files: File[] | undefined,
  idToken: string | undefined,
  signal: AbortSignal,
  onMessage?: (data: ServerMessage) => void,
  onOpen?: () => void
): Promise<Response> {
  if (!idToken) {
    return Promise.reject(new Error("Missing authentication token."))
  }

  const wsClient = new WebSocketClient({
    url: `${BACKEND_URI}/ws/start-research`,
    idToken,
    requestData: request,
    files,
    signal,
    onHeartbeat: (timestamp: number) => {
      logger.debug(`Heartbeat received at ${new Date(timestamp * 1000).toISOString()}`)
    },
    onOpen,
    onMessage,
  })

  return wsClient.createStreamingResponse()
}

/**
 * Fire-and-forget call to warm backend caches (system settings, company info,
 * this user's memory). Called on Explorer page mount so the first chat
 * request hits warm data.
 *
 * Uses plain fetch instead of fetchWithAuthRetry because this is non-critical:
 * a 401 here must NOT trigger the auth-retry/logout chain (which would cause a
 * sign-in loop when the MSAL token isn't available yet at mount time).
 *
 * Takes a `getToken` callback rather than a plain token string so the single
 * retry (below) re-reads the token instead of repeating a stale/missing one —
 * this is what actually fixes the prod 401: the caller fires this on mount,
 * and on the (rare) render where the MSAL token hasn't landed in
 * SettingsService yet, the retry gets a second chance to pick it up.
 */
export async function warmExplorerCachesApi(getToken?: () => string | undefined): Promise<void> {
  const now = Date.now()
  if (now - lastExplorerWarmAtMs < EXPLORER_WARM_MIN_INTERVAL_MS) {
    return
  }
  lastExplorerWarmAtMs = now

  const attempt = async (): Promise<boolean> => {
    try {
      const response = await fetch(`${BACKEND_URI}/explorer/warm`, {
        method: "GET",
        headers: getHeaders(getToken?.()),
      })
      return response.ok
    } catch {
      return false
    }
  }

  if (!(await attempt())) {
    // Single silent retry after a short delay.
    await new Promise((resolve) => setTimeout(resolve, EXPLORER_WARM_RETRY_DELAY_MS))
    await attempt()
  }
}
